import clsx from "clsx";
import {
  type DialogHTMLAttributes,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type SyntheticEvent,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";

import { Button, ButtonProps } from "../Button";
import { SubtleButtonExternalLink, SubtleButtonExternalLinkProps } from "../SubtleButton";
import { Text, Variant as TextVariant } from "../Text";
import { Title } from "../Title";
import styles from "./index.module.css";

export type DialogMessage = {
  text: ReactNode;
  alignLeft?: boolean;
  color?: DialogMessageColor;
};

export type DialogMessageColor = TextVariant;

export type DialogButton = Pick<ButtonProps, "disabled" | "loading" | "onClick"> & {
  label: string;
};

export type DialogExternalLinkButton = Pick<SubtleButtonExternalLinkProps, "href" | "disabled"> & {
  label: string;
};

export type DialogProps = Omit<DialogHTMLAttributes<HTMLDialogElement>, "open" | "onCancel" | "content"> & {
  open: boolean;
  disableCloseOnEscape?: boolean;
  title: ReactNode;
  primaryMessage?: DialogMessage;
  secondaryMessage?: DialogMessage;
  externalLinkButton?: DialogExternalLinkButton;
  isVerticalButton?: boolean;
  primaryButton?: DialogButton;
  secondaryButton?: DialogButton;
};

/**
 * レイアウトがある程度固定された確認用ダイアログ。
 *
 * @remarks
 * 「確認」「注意喚起」「はい / いいえ」のような用途で使う。
 * 自由なレイアウトが必要な場合は Modal を使う。
 *
 * @example
 * <Dialog
 *   open={isOpen}
 *   title="削除確認"
 *   primaryMessage={{ text: "削除してよろしいですか？" }}
 *   primaryButton={{ label: "削除する", onClick: onConfirm }}
 *   secondaryButton={{ label: "閉じる", onClick: onClose }}
 * />
 */
export const Dialog = ({
  open,
  disableCloseOnEscape = false,
  title,
  primaryMessage,
  secondaryMessage,
  externalLinkButton,
  isVerticalButton,
  primaryButton,
  secondaryButton,
  children,
  className,
  ...props
}: DialogProps) => {
  const [mounted, setMounted] = useState(false);
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const handleCancel = useCallback((event: Event | SyntheticEvent<HTMLDialogElement, Event>) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);
  const handleEscapeKeyDown = useCallback((event: KeyboardEvent<HTMLDialogElement>) => {
    if (event.key !== "Escape") {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
  }, []);

  useEffect(() => {
    // TODO: Gatsby build の SSR では document がないため portal を mount 後に限定している。Next.js 移行時に不要か見直す。
    setMounted(true);
  }, []);

  useEffect(() => {
    const dialog = dialogRef.current;
    if (!mounted || !dialog) return;

    if (open) {
      if (dialog.open) {
        return;
      }

      if (typeof dialog.showModal === "function") {
        dialog.showModal();
        // TODO: showModal() がダイアログ内要素に自動でフォーカスを移す挙動を暫定的に抑制している。
        requestAnimationFrame(() => {
          if (document.activeElement instanceof HTMLElement && dialog.contains(document.activeElement)) {
            document.activeElement.blur();
          }
        });
        return;
      }

      dialog.setAttribute("open", "");
      return;
    }

    if (dialog.open && typeof dialog.close === "function") {
      dialog.close();
      return;
    }

    dialog.removeAttribute("open");
  }, [mounted, open]);

  useEffect(() => {
    const dialog = dialogRef.current;
    return () => {
      if (dialog?.open && typeof dialog.close === "function") {
        dialog.close();
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handleDocumentKeyDown = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") {
        return;
      }

      const dialog = dialogRef.current;
      if (!dialog) {
        return;
      }

      const openDialogs = Array.from(document.querySelectorAll("dialog[open]"));
      if (openDialogs.at(-1) !== dialog) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      if (disableCloseOnEscape || secondaryButton?.disabled || secondaryButton?.loading) {
        return;
      }
      secondaryButton?.onClick?.({} as ReactMouseEvent<HTMLButtonElement>);
    };

    document.addEventListener("keydown", handleDocumentKeyDown, true);
    return () => {
      document.removeEventListener("keydown", handleDocumentKeyDown, true);
    };
  }, [disableCloseOnEscape, open, secondaryButton]);

  if (!open || !mounted) {
    return null;
  }

  return createPortal(
    <dialog
      {...props}
      ref={dialogRef}
      aria-modal="true"
      aria-labelledby={titleId}
      className={clsx(styles.dialog, className)}
      onCancel={handleCancel}
      onKeyDownCapture={handleEscapeKeyDown}
    >
      <Title id={titleId} fontSize="xm" textAlign="center" as="h2">
        {title}
      </Title>
      {primaryMessage && (
        <Text variant={primaryMessage.color} className={clsx(primaryMessage.alignLeft ? "text-left" : "text-center")}>
          {primaryMessage.text}
        </Text>
      )}
      {secondaryMessage && (
        <Text
          variant={secondaryMessage.color ?? "caution"}
          className={clsx(secondaryMessage.alignLeft ? "text-left" : "text-center")}
        >
          {secondaryMessage.text}
        </Text>
      )}
      {externalLinkButton && (
        <SubtleButtonExternalLink
          href={externalLinkButton.href}
          title={externalLinkButton.label}
          align="center"
          disabled={externalLinkButton.disabled}
        >
          {externalLinkButton.label}
        </SubtleButtonExternalLink>
      )}
      {children}
      {(primaryButton || secondaryButton) && (
        <div className={clsx("mt-2 flex items-center justify-center gap-2", isVerticalButton && "flex-col-reverse")}>
          {secondaryButton && (
            <Button
              variant="outlined"
              onClick={secondaryButton.onClick}
              // TODO: showModal() 呼び出し時のフォーカス移動によるスクロール発生を暫定的に抑制している。
              // autoFocus={shouldAutoFocusSecondaryButton}
              disabled={secondaryButton.disabled}
              loading={secondaryButton.loading}
            >
              {secondaryButton.label}
            </Button>
          )}
          {primaryButton && (
            <Button
              variant="solid"
              color="key"
              onClick={primaryButton.onClick}
              // TODO: showModal() 呼び出し時のフォーカス移動によるスクロール発生を暫定的に抑制している。
              // autoFocus={shouldAutoFocusPrimaryButton}
              disabled={primaryButton.disabled}
              loading={primaryButton.loading}
            >
              {primaryButton.label}
            </Button>
          )}
        </div>
      )}
    </dialog>,
    document.body
  );
};
